import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import projects from '../../data/projectsData';
import CopyButton from '../partials/CopyButton';

const BlockRenderer = ({ block }) => {
  switch (block.type) {
    case 'heading': {
      const Tag = block.level === 2 ? H2 : H3;
      return <Tag>{block.text}</Tag>;
    }
    case 'paragraph':
      return <Paragraph>{block.text}</Paragraph>;
    case 'code':
      return (
        <CodeBlock>
          <CodeHeader>
            <LangLabel>{block.lang}</LangLabel>
            <CopyButton text={block.code} />
          </CodeHeader>
          <SyntaxHighlighter
            language={block.lang}
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              borderRadius: '0 0 6px 6px',
              background: 'var(--bg-surface-2)',
              fontSize: '0.875rem',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {block.code}
          </SyntaxHighlighter>
        </CodeBlock>
      );
    case 'list': {
      const ListEl = block.ordered ? OL : UL;
      return (
        <ListEl>
          {block.items.map((item, i) => <li key={i}>{item}</li>)}
        </ListEl>
      );
    }
    case 'callout':
      return <Callout>{block.text}</Callout>;
    default:
      return null;
  }
};

const ProjectPage = () => {
  const { slug } = useParams();
  const project = projects.find(p => p.slug === slug);

  useEffect(() => {
    if (project) document.title = `${project.title} - Dmitri Malisev`;
    else document.title = 'Project not found - Dmitri Malisev';
  }, [project]);

  if (!project) {
    return (
      <Container>
        <BackLink to="/projects">← Projects</BackLink>
        <NotFound>Project not found.</NotFound>
      </Container>
    );
  }

  return (
    <Container>
      <BackLink to="/projects">← Projects</BackLink>

      <ProjectHeader>
        <ProjectMeta>
    { /* <ProjectYear>{project.year}</ProjectYear> */ }
          <ProjectTags>
            {project.tags.map(tag => <ProjectTag key={tag}>{tag}</ProjectTag>)}
          </ProjectTags>
          <GithubLink href={project.url} target="_blank" rel="noopener noreferrer">
            GitHub →
          </GithubLink>
        </ProjectMeta>
        <ProjectTitle>{project.title}</ProjectTitle>
        <ProjectDesc>{project.shortDescription}</ProjectDesc>
      </ProjectHeader>

      <ProjectBody>
        {project.blocks.map((block, i) => (
          <BlockRenderer key={i} block={block} />
        ))}
      </ProjectBody>
    </Container>
  );
};

const Container = styled.div`
  padding: 3rem 0 4rem;
  max-width: 740px;
`;

const BackLink = styled(Link)`
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  color: var(--text-muted);
  text-decoration: none;
  display: inline-block;
  margin-bottom: 2.5rem;
  transition: color 0.15s;

  &:hover {
    color: var(--text-primary);
  }
`;

const NotFound = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
`;

const ProjectHeader = styled.div`
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid var(--border);
`;

const ProjectMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;


const ProjectTags = styled.div`
  display: flex;
  gap: 0.375rem;
`;

const ProjectTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  padding: 0.15rem 0.45rem;
  border: 1px solid var(--border);
  border-radius: 3px;
  color: var(--text-muted);
`;

const GithubLink = styled.a`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--accent);
  text-decoration: none;
  margin-left: auto;
  transition: color 0.15s;

  &:hover {
    color: var(--accent-hover);
  }
`;

const ProjectTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.75rem;
  letter-spacing: -0.02em;
  line-height: 1.3;

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const ProjectDesc = styled.p`
  font-size: 1.0625rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.7;
`;

const ProjectBody = styled.div`
  font-size: 1rem;
  line-height: 1.8;
`;

const H2 = styled.h2`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 2.5rem 0 1rem;
  letter-spacing: -0.01em;
`;

const H3 = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 2rem 0 0.75rem;
`;

const Paragraph = styled.p`
  color: var(--text-muted);
  margin: 0 0 1.25rem;
`;

const CodeBlock = styled.div`
  margin: 1.5rem 0;
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
`;

const CodeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border);
`;

const LangLabel = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: lowercase;
`;

const UL = styled.ul`
  color: var(--text-muted);
  padding-left: 1.5rem;
  margin: 0 0 1.25rem;

  li {
    margin-bottom: 0.375rem;
  }
`;

const OL = styled.ol`
  color: var(--text-muted);
  padding-left: 1.5rem;
  margin: 0 0 1.25rem;

  li {
    margin-bottom: 0.375rem;
  }
`;

const Callout = styled.div`
  padding: 1rem 1.25rem;
  border-left: 3px solid var(--accent);
  background: var(--accent-subtle);
  color: var(--text-muted);
  font-size: 0.9375rem;
  line-height: 1.7;
  margin: 1.5rem 0;
  border-radius: 0 4px 4px 0;
`;

export default ProjectPage;
